/**
 * Centralized Shadow Definitions for Autobet
 * Provides custom shadow presets matching Autobet design specifications,
 * along with a 25-level elevation array for Material UI.
 */

// Custom Semantic Shadows
export const customShadows = {
  none: "none",

  // Dashboard & Card Elevations
  card: "0 2px 8px -2px rgba(15, 23, 42, 0.05), 0 1px 4px -1px rgba(15, 23, 42, 0.03)",
  cardHover: "0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.03)",
  cardSubtle: "0 2px 8px -2px rgba(15, 23, 42, 0.05)",
  cardFlat: "0 1px 2px rgba(0, 0, 0, 0.02)",

  // Legacy Project-specific CSS shadows
  cardLegacy: "0 0 16px 1px rgba(139, 139, 139, 0.25)",
  cardStrong: "0 0 16px 1px rgba(139, 139, 139, 0.918)", // from buypage.css .buy-card
  innerCard: "0 0 16px 1px rgba(212, 128, 128, 0.35)",  // from buypage.css .card-inner
  outerCard: "0 0 16px 1px rgba(0, 0, 0, 0.15)",       // from buypage.css .outer-card

  // Dark Ribbon & Surface Elevations
  ribbon: "0 10px 30px rgba(0, 0, 0, 0.25)",
  dialog: "0 10px 30px rgba(0, 0, 0, 0.08)",
  dropdownMenu: "0 10px 30px rgba(0, 0, 0, 0.15)",
  headerMenu: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",

  // Button & Action Glows
  buttonPrimary: "0 2px 6px rgba(37, 99, 235, 0.3)",
  buttonPrimaryHover: "0 4px 12px rgba(37, 99, 235, 0.4)",
  buttonActionHover: "0 2px 5px rgba(37, 99, 235, 0.08)",
  buttonDarkHover: "0 2px 6px rgba(13, 21, 39, 0.2)",

  // Brand & Badges
  emblemRed: "0 2px 8px rgba(239, 68, 68, 0.4)",

  // Modern UI elevations
  xs: "0 1px 3px rgba(0, 42, 51, 0.08)",
  sm: "0 2px 8px rgba(0, 42, 51, 0.08)",
  md: "0 4px 16px rgba(0, 42, 51, 0.1)",
  lg: "0 8px 24px rgba(0, 42, 51, 0.12)",
  xl: "0 16px 36px rgba(0, 42, 51, 0.16)",

  // Glow shadows for buttons / focus states
  primaryGlow: "0 4px 20px 0 rgba(252, 157, 46, 0.4)",
  primaryGlowSmall: "0 2px 10px 0 rgba(252, 157, 46, 0.3)",
  secondaryGlow: "0 4px 20px 0 rgba(0, 42, 51, 0.3)",
  dangerGlow: "0 4px 16px 0 rgba(244, 67, 54, 0.35)",
  successGlow: "0 4px 16px 0 rgba(76, 175, 80, 0.35)",

  // Overlays, Dropdowns & Modals
  dropdown: "0 10px 30px -5px rgba(0, 42, 51, 0.18)",
  modal: "0 20px 50px -10px rgba(0, 42, 51, 0.3)",
};

/**
 * 25-level elevation array for Material UI theme
 */
export const muiShadows = [
  "none",
  "0 1px 3px rgba(0, 42, 51, 0.06), 0 1px 2px rgba(0, 42, 51, 0.04)",
  "0 2px 6px rgba(0, 42, 51, 0.07), 0 1px 3px rgba(0, 42, 51, 0.05)",
  "0 3px 8px rgba(0, 42, 51, 0.08), 0 2px 4px rgba(0, 42, 51, 0.05)",
  "0 4px 12px rgba(0, 42, 51, 0.09), 0 2px 6px rgba(0, 42, 51, 0.06)",
  "0 6px 16px rgba(0, 42, 51, 0.10), 0 3px 8px rgba(0, 42, 51, 0.07)",
  "0 8px 20px rgba(0, 42, 51, 0.11), 0 4px 10px rgba(0, 42, 51, 0.08)",
  "0 10px 24px rgba(0, 42, 51, 0.12), 0 5px 12px rgba(0, 42, 51, 0.08)",
  "0 12px 28px rgba(0, 42, 51, 0.13), 0 6px 14px rgba(0, 42, 51, 0.09)",
  "0 14px 32px rgba(0, 42, 51, 0.14), 0 7px 16px rgba(0, 42, 51, 0.09)",
  "0 16px 36px rgba(0, 42, 51, 0.15), 0 8px 18px rgba(0, 42, 51, 0.10)",
  "0 18px 40px rgba(0, 42, 51, 0.16), 0 9px 20px rgba(0, 42, 51, 0.10)",
  "0 20px 44px rgba(0, 42, 51, 0.17), 0 10px 22px rgba(0, 42, 51, 0.11)",
  "0 22px 48px rgba(0, 42, 51, 0.18), 0 11px 24px rgba(0, 42, 51, 0.11)",
  "0 24px 52px rgba(0, 42, 51, 0.19), 0 12px 26px rgba(0, 42, 51, 0.12)",
  "0 26px 56px rgba(0, 42, 51, 0.20), 0 13px 28px rgba(0, 42, 51, 0.12)",
  "0 28px 60px rgba(0, 42, 51, 0.21), 0 14px 30px rgba(0, 42, 51, 0.13)",
  "0 30px 64px rgba(0, 42, 51, 0.22), 0 15px 32px rgba(0, 42, 51, 0.13)",
  "0 32px 68px rgba(0, 42, 51, 0.23), 0 16px 34px rgba(0, 42, 51, 0.14)",
  "0 34px 72px rgba(0, 42, 51, 0.24), 0 17px 36px rgba(0, 42, 51, 0.14)",
  "0 36px 76px rgba(0, 42, 51, 0.25), 0 18px 38px rgba(0, 42, 51, 0.15)",
  "0 38px 80px rgba(0, 42, 51, 0.26), 0 19px 40px rgba(0, 42, 51, 0.15)",
  "0 40px 84px rgba(0, 42, 51, 0.27), 0 20px 42px rgba(0, 42, 51, 0.16)",
  "0 42px 88px rgba(0, 42, 51, 0.28), 0 21px 44px rgba(0, 42, 51, 0.16)",
  "0 44px 92px rgba(0, 42, 51, 0.29), 0 22px 46px rgba(0, 42, 51, 0.17)",
];

export default customShadows;
